import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Col,
    FormFeedback,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Table
} from "reactstrap";

const LEVELS_WITHOUT_HOREN = ["A1", "A2"];

const normalizeText = (value) => (value || "").trim();

const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());
};

const isValidBirthDate = (value) => {
    if (!value) return false;
    return /^\d{4}-\d{2}-\d{2}$/.test(value);
};

const getUniqueParticipants = (groups = []) => {
    const map = new Map();

    groups.forEach((group) => {
        const partecipants = Array.isArray(group?.partecipants) ? group.partecipants : [];

        partecipants.forEach((p) => {
            // I corsisti che hanno abbandonato non sono selezionabili
            if (p.enrollment && p.enrollment.result === "ABANDONED") return;

            const normalized = {
                id: p.id,
                firstname: normalizeText(p.firstname),
                lastname: normalizeText(p.lastname),
                email: normalizeText(p.email),
                cell: normalizeText(p.cell),
                birth_place: normalizeText(p.birth_place),
                birth_date: normalizeText(p.birth_date),
                active: !!p.active,
                consulent: p.consulent || "",
                groups: [group?.id].filter(Boolean),
            };

            if (!map.has(p.id)) {
                map.set(p.id, normalized);
            } else {
                const prev = map.get(p.id);
                map.set(p.id, {
                    ...prev,
                    email: prev.email || normalized.email,
                    cell: prev.cell || normalized.cell,
                    birth_place: prev.birth_place || normalized.birth_place,
                    birth_date: prev.birth_date || normalized.birth_date,
                    groups: [...new Set([...(prev.groups || []), ...(group?.id ? [group.id] : [])])]
                });
            }
        });
    });

    return Array.from(map.values()).sort((a, b) => {
        const nameA = `${a.firstname} ${a.lastname}`.toLowerCase();
        const nameB = `${b.firstname} ${b.lastname}`.toLowerCase();
        return nameA.localeCompare(nameB);
    });
};

const buildInitialForm = (participants = []) => {
    const data = {};

    participants.forEach((p) => {
        data[p.id] = {
            selected: false,
            firstname: normalizeText(p.firstname),
            lastname: normalizeText(p.lastname),
            email: normalizeText(p.email),
            birth_place: normalizeText(p.birth_place),
            birth_date: normalizeText(p.birth_date),
            scores: {
                lesen: "",
                sprechen: "",
                schreiben: "",
                horen: ""
            }
        };
    });

    return data;
};

const CompleteClassroomModal = ({ isOpen, toggle, classroom, onSubmit }) => {
    const [step, setStep] = useState(1);
    const [form, setForm] = useState({});
    const [submitError, setSubmitError] = useState("");

    const participants = useMemo(() => {
        return getUniqueParticipants(classroom?.groups || []);
    }, [classroom]);

    const levelName = useMemo(() => {
        return normalizeText(classroom?.level?.name).toUpperCase();
    }, [classroom]);

    const showHoren = useMemo(() => {
        return !LEVELS_WITHOUT_HOREN.includes(levelName);
    }, [levelName]);

    // Livello finale (A1, A2, B1.3, ecc.): chiede anagrafica + voti e genera attestato.
    // Modulo intermedio (B1.1, B1.2, ecc.): solo selezione corsisti.
    const isFinalModule = !!classroom?.level?.is_final_module;
    const totalSteps = isFinalModule ? 3 : 1;

    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setSubmitError("");
            setForm(buildInitialForm(participants));
        }
    }, [isOpen, participants]);

    const selectedParticipants = useMemo(() => {
        return participants.filter((p) => form?.[p.id]?.selected);
    }, [participants, form]);

    const selectedCount = selectedParticipants.length;

    const updateField = (participantId, field, value) => {
        setForm((prev) => ({
            ...prev,
            [participantId]: {
                ...prev[participantId],
                [field]: value
            }
        }));
    };

    const updateScore = (participantId, scoreKey, value) => {
        let parsed = value;

        if (parsed !== "") {
            parsed = Number(parsed);
            if (Number.isNaN(parsed)) parsed = "";
            if (parsed !== "" && parsed < 1) parsed = 1;
            if (parsed !== "" && parsed > 10) parsed = 10;
        }

        setForm((prev) => ({
            ...prev,
            [participantId]: {
                ...prev[participantId],
                scores: {
                    ...prev[participantId].scores,
                    [scoreKey]: parsed
                }
            }
        }));
    };

    const toggleSelected = (participantId) => {
        setForm((prev) => ({
            ...prev,
            [participantId]: {
                ...prev[participantId],
                selected: !prev[participantId].selected
            }
        }));
    };

    const allSelected = participants.length > 0 && participants.every((p) => form?.[p.id]?.selected);

    const toggleSelectAll = () => {
        const newValue = !allSelected;
        setForm((prev) => {
            const next = { ...prev };
            participants.forEach((p) => {
                if (next[p.id]) next[p.id] = { ...next[p.id], selected: newValue };
            });
            return next;
        });
    };

    const validateStep1 = () => {
        if (selectedParticipants.length === 0) {
            setSubmitError("Seleziona almeno un corsista che ha completato correttamente il corso.");
            return false;
        }

        setSubmitError("");
        return true;
    };

    const validateStep2 = () => {
        if (selectedParticipants.length === 0) {
            setSubmitError("Non ci sono corsisti selezionati.");
            return false;
        }

        for (const participant of selectedParticipants) {
            const current = form[participant.id];

            if (!normalizeText(current.firstname)) {
                setSubmitError(`Il nome è obbligatorio per ${participant.firstname} ${participant.lastname}.`);
                return false;
            }

            if (!normalizeText(current.lastname)) {
                setSubmitError(`Il cognome è obbligatorio per ${participant.firstname} ${participant.lastname}.`);
                return false;
            }

            if (!normalizeText(current.email)) {
                setSubmitError(`L'email è obbligatoria per ${participant.firstname} ${participant.lastname}.`);
                return false;
            }

            if (!isValidEmail(current.email)) {
                setSubmitError(`L'email inserita non è valida per ${participant.firstname} ${participant.lastname}.`);
                return false;
            }

            if (!normalizeText(current.birth_place)) {
                setSubmitError(`Il luogo di nascita è obbligatorio per ${participant.firstname} ${participant.lastname}.`);
                return false;
            }

            if (!normalizeText(current.birth_date)) {
                setSubmitError(`La data di nascita è obbligatoria per ${participant.firstname} ${participant.lastname}.`);
                return false;
            }

            if (!isValidBirthDate(current.birth_date)) {
                setSubmitError(`La data di nascita deve essere nel formato YYYY-MM-DD per ${participant.firstname} ${participant.lastname}.`);
                return false;
            }
        }

        setSubmitError("");
        return true;
    };

    const validateStep3 = () => {
        if (selectedParticipants.length === 0) {
            setSubmitError("Non ci sono corsisti selezionati.");
            return false;
        }

        for (const participant of selectedParticipants) {
            const currentScores = form[participant.id]?.scores || {};
            const requiredKeys = showHoren
                ? ["lesen", "sprechen", "schreiben", "horen"]
                : ["lesen", "sprechen", "schreiben"];

            for (const key of requiredKeys) {
                const value = currentScores[key];

                if (value === "" || value === null || typeof value === "undefined") {
                    setSubmitError(`Tutti i voti sono obbligatori per ${form[participant.id].firstname} ${form[participant.id].lastname}.`);
                    return false;
                }

                const num = Number(value);

                if (Number.isNaN(num) || num < 1 || num > 10) {
                    setSubmitError(`I voti devono essere compresi tra 1 e 10 per ${form[participant.id].firstname} ${form[participant.id].lastname}.`);
                    return false;
                }
            }
        }

        setSubmitError("");
        return true;
    };

    const goNext = () => {
        if (step === 1 && !validateStep1()) return;
        if (step === 2 && !validateStep2()) return;
        setStep((prev) => prev + 1);
    };

    const goBack = () => {
        setSubmitError("");
        setStep((prev) => prev - 1);
    };

    const buildPayload = () => {
        // Modulo intermedio: serve solo l'elenco dei corsisti che hanno passato
        if (!isFinalModule) {
            return {
                id_classroom: classroom.id,
                enrollments: selectedParticipants.map((participant) => ({
                    id_partecipant: participant.id
                }))
            };
        }

        return {
            id_classroom: classroom.id,
            enrollments: selectedParticipants.map((participant) => {
                const current = form[participant.id];

                return {
                    id_partecipant: participant.id,
                    firstname: normalizeText(current.firstname),
                    lastname: normalizeText(current.lastname),
                    email: normalizeText(current.email),
                    birth_place: normalizeText(current.birth_place),
                    birth_date: normalizeText(current.birth_date),
                    scores: {
                        lesen: Number(current.scores.lesen),
                        sprechen: Number(current.scores.sprechen),
                        schreiben: Number(current.scores.schreiben),
                        ...(showHoren ? { horen: Number(current.scores.horen) } : {})
                    }
                };
            })
        };
    };

    const handleConfirm = () => {
        if (isFinalModule) {
            if (!validateStep3()) return;
        } else {
            if (!validateStep1()) return;
        }

        const payload = buildPayload();

        if (typeof onSubmit === "function") {
            onSubmit(payload);
        }
    };

    const renderStep1 = () => (
        <div>
            <Alert color="info" className="mb-3">
                {isFinalModule
                    ? <span><b>Step 1 di 3</b> — Seleziona i corsisti che hanno <b>completato correttamente</b> il corso.</span>
                    : <span><b>Modulo intermedio</b> — Seleziona i corsisti che hanno <b>completato correttamente</b> il modulo. Voti e attestato verranno gestiti al modulo finale del livello.</span>}
            </Alert>

            <div className="table-responsive">
                <Table bordered hover align="middle" className="mb-0">
                    <thead>
                    <tr>
                        <th style={{ width: 70 }} className="text-center">
                            <Input
                                type="checkbox"
                                checked={allSelected}
                                disabled={participants.length === 0}
                                onChange={toggleSelectAll}
                                title="Seleziona tutti"
                            />
                        </th>
                        <th>Corsista</th>
                        <th>Email</th>
                        <th>Data nascita</th>
                        <th>Luogo nascita</th>
                        <th>Stato utente</th>
                    </tr>
                    </thead>
                    <tbody>
                    {participants.map((participant) => (
                        <tr key={participant.id}>
                            <td className="text-center">
                                <Input
                                    type="checkbox"
                                    checked={!!form?.[participant.id]?.selected}
                                    onChange={() => toggleSelected(participant.id)}
                                />
                            </td>
                            <td>
                                <b>{participant.firstname} {participant.lastname}</b>
                            </td>
                            <td>{participant.email || <span className="text-danger">Email mancante</span>}</td>
                            <td>{participant.birth_date || <span className="text-danger">Mancante</span>}</td>
                            <td>{participant.birth_place || <span className="text-danger">Mancante</span>}</td>
                            <td>
                                {participant.active
                                    ? <span className="badge bg-success">Attivo</span>
                                    : <span className="badge bg-danger">Non attivo</span>}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div>
            <Alert color="info" className="mb-3">
                <b>Step 2 di 3</b> — Verifica e correggi <b>nome, cognome, email, luogo di nascita e data di nascita</b>.
            </Alert>

            {selectedParticipants.length === 0 ? (
                <Alert color="warning" className="mb-0">
                    Nessun corsista selezionato.
                </Alert>
            ) : (
                <div className="d-flex flex-column gap-3">
                    {selectedParticipants.map((participant) => {
                        const current = form[participant.id];
                        const firstnameInvalid = !normalizeText(current.firstname);
                        const lastnameInvalid = !normalizeText(current.lastname);
                        const emailInvalid = !normalizeText(current.email) || !isValidEmail(current.email);
                        const birthPlaceInvalid = !normalizeText(current.birth_place);
                        const birthDateInvalid = !normalizeText(current.birth_date) || !isValidBirthDate(current.birth_date);

                        return (
                            <div className="border rounded p-3" key={participant.id}>
                                <h6 className="mb-3">
                                    <b>{participant.firstname} {participant.lastname}</b>
                                </h6>

                                <Row className="g-3">
                                    <Col md={4}>
                                        <Label className="form-label">Nome</Label>
                                        <Input
                                            value={current.firstname}
                                            invalid={firstnameInvalid}
                                            onChange={(e) => updateField(participant.id, "firstname", e.target.value)}
                                        />
                                        <FormFeedback>Il nome è obbligatorio.</FormFeedback>
                                    </Col>

                                    <Col md={4}>
                                        <Label className="form-label">Cognome</Label>
                                        <Input
                                            value={current.lastname}
                                            invalid={lastnameInvalid}
                                            onChange={(e) => updateField(participant.id, "lastname", e.target.value)}
                                        />
                                        <FormFeedback>Il cognome è obbligatorio.</FormFeedback>
                                    </Col>

                                    <Col md={4}>
                                        <Label className="form-label">Email</Label>
                                        <Input
                                            type="email"
                                            value={current.email}
                                            invalid={emailInvalid}
                                            onChange={(e) => updateField(participant.id, "email", e.target.value)}
                                        />
                                        <FormFeedback>Inserisci un'email valida.</FormFeedback>
                                    </Col>

                                    <Col md={6}>
                                        <Label className="form-label">Luogo di nascita</Label>
                                        <Input
                                            value={current.birth_place}
                                            invalid={birthPlaceInvalid}
                                            onChange={(e) => updateField(participant.id, "birth_place", e.target.value)}
                                        />
                                        <FormFeedback>Il luogo di nascita è obbligatorio.</FormFeedback>
                                    </Col>

                                    <Col md={6}>
                                        <Label className="form-label">Data di nascita</Label>
                                        <Input
                                            type="date"
                                            value={current.birth_date}
                                            invalid={birthDateInvalid}
                                            onChange={(e) => updateField(participant.id, "birth_date", e.target.value)}
                                        />
                                        <FormFeedback>La data di nascita è obbligatoria.</FormFeedback>
                                    </Col>
                                </Row>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );

    const renderScoreInput = (participantId, scoreKey, label) => {
        const value = form?.[participantId]?.scores?.[scoreKey] ?? "";
        const invalid = value === "" || Number(value) < 1 || Number(value) > 10;

        return (
            <Col md={showHoren ? 3 : 4}>
                <Label className="form-label">{label}</Label>
                <Input
                    type="number"
                    min={1}
                    max={10}
                    step={1}
                    value={value}
                    invalid={invalid}
                    onChange={(e) => updateScore(participantId, scoreKey, e.target.value)}
                />
                <FormFeedback>Valore obbligatorio da 1 a 10.</FormFeedback>
            </Col>
        );
    };

    const renderStep3 = () => (
        <div>
            <Alert color="info" className="mb-3">
                <b>Step 3 di 3</b> — Inserisci i voti finali dei corsisti selezionati.
                <br />
                Livello classe: <b>{classroom?.level?.name || "-"}</b>
                <br />
                Voti richiesti: <b>{showHoren ? "LESEN / SPRECHEN / SCHREIBEN / HÖREN" : "LESEN / SPRECHEN / SCHREIBEN"}</b>
            </Alert>

            {selectedParticipants.length === 0 ? (
                <Alert color="warning" className="mb-0">
                    Nessun corsista selezionato.
                </Alert>
            ) : (
                <div className="d-flex flex-column gap-3">
                    {selectedParticipants.map((participant) => (
                        <div className="border rounded p-3" key={participant.id}>
                            <h6 className="mb-3">
                                <b>{form[participant.id].firstname} {form[participant.id].lastname}</b>
                                <div className="text-muted small">{form[participant.id].email}</div>
                                <div className="text-muted small">
                                    {form[participant.id].birth_place} - {form[participant.id].birth_date}
                                </div>
                            </h6>

                            <Row className="g-3">
                                {renderScoreInput(participant.id, "lesen", "LESEN")}
                                {renderScoreInput(participant.id, "sprechen", "SPRECHEN")}
                                {renderScoreInput(participant.id, "schreiben", "SCHREIBEN")}
                                {showHoren ? renderScoreInput(participant.id, "horen", "HÖREN") : null}
                            </Row>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" centered>
            <ModalHeader toggle={toggle}>
                Completa classe
            </ModalHeader>

            <ModalBody>
                {(isFinalModule) ? <div className="mb-3">
                    <div className="d-flex flex-wrap gap-2">
                        <span className={`badge ${step === 1 ? "bg-primary" : "bg-light text-dark"}`}>1. Selezione</span>
                        <span className={`badge ${step === 2 ? "bg-primary" : "bg-light text-dark"}`}>2. Verifica dati</span>
                        <span className={`badge ${step === 3 ? "bg-primary" : "bg-light text-dark"}`}>3. Votazioni</span>
                    </div>
                </div> : null}

                {submitError ? <Alert color="danger">{submitError}</Alert> : null}

                {step === 1 && renderStep1()}
                {step === 2 && renderStep2()}
                {step === 3 && renderStep3()}
            </ModalBody>

            <ModalFooter className="d-flex justify-content-between">
                <div className="text-muted small">
                    Corsisti selezionati: <b>{selectedCount}</b>
                </div>

                <div className="d-flex gap-2">
                    <Button color="light" onClick={toggle}>
                        Annulla
                    </Button>

                    {step > 1 ? (
                        <Button color="secondary" onClick={goBack}>
                            Indietro
                        </Button>
                    ) : null}

                    {step < totalSteps ? (
                        <Button color="primary" onClick={goNext}>
                            Avanti
                        </Button>
                    ) : (
                        <Button color="success" onClick={handleConfirm}>
                            Conferma
                        </Button>
                    )}
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default CompleteClassroomModal;
